import { useState, useEffect } from "react";
import Topbar from "../components/layout/Topbar.jsx";
import {
  getAdmins,
  createAdmin,
  updateAdmin,
  toggleAdminActive,
  deleteAdmin,
  ADMIN_PERMISSIONS,
} from "../services/systemService.js";

// ─────────────────────────────────────────────
//  COMPOSANT — FORMULAIRE CRÉATION / ÉDITION SOUS-ADMIN
//
//  Permet au SUPER_ADMIN de créer un compte sous-admin
//  et de choisir précisément quelles permissions ADMIN_*
//  ce sous-admin aura.
// ─────────────────────────────────────────────

function AdminFormModal({ editData, onSave, onClose }) {
  const isEdit = !!editData;

  const [form, setForm] = useState({
    firstName: editData?.firstName || "",
    lastName:  editData?.lastName  || "",
    email:     editData?.email     || "",
    password:  "",
  });

  // Permissions ADMIN_* cochées pour ce sous-admin
  const [selected, setSelected] = useState(
    new Set((editData?.permissionOverrides || []).map(p => p.name))
  );

  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const togglePerm = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  // Sélectionner / désélectionner toutes les permissions
  const toggleAll = () => {
    if (selected.size === ADMIN_PERMISSIONS.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(ADMIN_PERMISSIONS.map(p => p.name)));
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.firstName.trim()) return setError("Le prénom est obligatoire.");
    if (!form.lastName.trim())  return setError("Le nom est obligatoire.");
    if (!form.email.trim())     return setError("L'email est obligatoire.");
    if (!isEdit && !form.password.trim()) return setError("Le mot de passe est obligatoire.");
    if (selected.size === 0)    return setError("Sélectionnez au moins une permission.");

    setLoading(true);
    try {
      const payload = {
        ...form,
        permissionOverrideNames: [...selected],
      };

      if (isEdit) {
        await updateAdmin(editData.id, payload);
      } else {
        await createAdmin(payload);
      }

      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 520 }}>

        <div className="modal-header">
          <h3>{isEdit ? "Modifier le sous-admin" : "Nouveau sous-admin"}</h3>
          <button className="modal-close" onClick={onClose}><i className="bi bi-x-lg"/></button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="form-error">
              <i className="bi bi-exclamation-triangle me-2"/>{error}
            </div>
          )}

          {/* Prénom + Nom */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="form-group">
              <label className="itm-label">Prénom *</label>
              <input className="itm-input" value={form.firstName}
                onChange={e => set("firstName", e.target.value)} placeholder="Thomas"/>
            </div>
            <div className="form-group">
              <label className="itm-label">Nom *</label>
              <input className="itm-input" value={form.lastName}
                onChange={e => set("lastName", e.target.value)} placeholder="Leroux"/>
            </div>
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="itm-label">Email *</label>
            <input className="itm-input" type="email" value={form.email}
              onChange={e => set("email", e.target.value)}
              placeholder="thomas@itmanagerpro.com"/>
          </div>

          {/* Mot de passe */}
          <div className="form-group">
            <label className="itm-label">
              Mot de passe
              {isEdit && (
                <span style={{ color: "var(--text-3)", fontWeight: 400 }}>
                  {" "}(laisser vide pour ne pas changer)
                </span>
              )}
            </label>
            <div style={{ position: "relative" }}>
              <input
                className="itm-input" style={{ paddingRight: 44 }}
                type={showPwd ? "text" : "password"}
                value={form.password}
                onChange={e => set("password", e.target.value)}
                placeholder={isEdit ? "••••••••" : "Minimum 6 caractères"}
              />
              <button
                type="button" onClick={() => setShowPwd(v => !v)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-3)" }}
              >
                <i className={`bi ${showPwd ? "bi-eye-slash" : "bi-eye"}`}/>
              </button>
            </div>
          </div>

          {/* Permissions ADMIN_* */}
          <div className="form-group">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <label className="itm-label" style={{ margin: 0 }}>
                Accès accordés *
                <span style={{ marginLeft: 8, fontSize: 11, color: "var(--accent)", fontWeight: 400 }}>
                  {selected.size}/{ADMIN_PERMISSIONS.length} sélectionné{selected.size > 1 ? "s" : ""}
                </span>
              </label>
              <button
                type="button"
                onClick={toggleAll}
                style={{ fontSize: 11, background: "none", border: "none", cursor: "pointer", color: "var(--accent)", textDecoration: "underline" }}
              >
                {selected.size === ADMIN_PERMISSIONS.length ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
            </div>

            {/* Une ligne par permission admin */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {ADMIN_PERMISSIONS.map(perm => (
                <label
                  key={perm.name}
                  className="admin-perm-row"
                  style={{ background: selected.has(perm.name) ? "rgba(0,194,203,.07)" : "rgba(255,255,255,.03)" }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(perm.name)}
                    onChange={() => togglePerm(perm.name)}
                    style={{ marginRight: 10 }}
                  />
                  <div className="admin-perm-icon">
                    <i className={`bi ${perm.icon}`}/>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-1)" }}>
                      {perm.description}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-3)", fontFamily: "monospace" }}>
                      {perm.name}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading
              ? <><i className="bi bi-arrow-repeat spin me-1"/>Enregistrement…</>
              : isEdit ? "Mettre à jour" : "Créer le sous-admin"
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  PAGE PARAMÈTRES SYSTÈME
// ─────────────────────────────────────────────

export default function SettingsPage() {
  const [saved, setSaved] = useState(false);
  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  // ── Comptes admins ────────────────────────
  const [admins,     setAdmins]     = useState([]);
  const [loadingAdm, setLoadingAdm] = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [editAdmin,  setEditAdmin]  = useState(null);

  useEffect(() => { loadAdmins(); }, []);

  const loadAdmins = async () => {
    setLoadingAdm(true);
    try {
      const res = await getAdmins();
      setAdmins(res.data || []);
    } catch (err) {
      console.error("Erreur chargement admins :", err.message);
    } finally {
      setLoadingAdm(false);
    }
  };

  const handleToggle = async (admin) => {
    try {
      await toggleAdminActive(admin.id, !admin.active);
      setAdmins(prev => prev.map(a => a.id === admin.id ? { ...a, active: !admin.active } : a));
    } catch (err) { alert(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce sous-admin ?")) return;
    try {
      await deleteAdmin(id);
      setAdmins(prev => prev.filter(a => a.id !== id));
    } catch (err) { alert(err.message); }
  };

  return (
    <>
      <Topbar title="Paramètres" subtitle="système"/>
      <div className="page-body" style={{ maxWidth: 900 }}>
        <div className="page-header">
          <div>
            <div className="page-title">Paramètres <span>système</span></div>
            <div className="page-sub">Configuration globale de la plateforme</div>
          </div>
        </div>

        {/* ═══════════════════════════════════════
            SECTION — Comptes administrateurs
        ════════════════════════════════════════ */}
        <div className="itm-card anim-fade-up" style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div>
              <div className="card-title" style={{ marginBottom: 2 }}>Comptes administrateurs</div>
              <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                Le SUPER_ADMIN peut créer des sous-admins avec des accès limités à certaines fonctionnalités.
              </div>
            </div>
            <button className="btn-itm btn-itm-primary" onClick={() => { setEditAdmin(null); setShowForm(true); }}>
              <i className="bi bi-person-plus"/> Nouveau sous-admin
            </button>
          </div>

          {/* Liste des admins */}
          {loadingAdm ? (
            <div style={{ padding: 20, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
              <i className="bi bi-arrow-repeat spin me-2"/>Chargement…
            </div>
          ) : admins.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
              <i className="bi bi-people" style={{ fontSize: 28, opacity: .3, display: "block", marginBottom: 10 }}/>
              Aucun sous-admin créé. Vous êtes le seul administrateur.
            </div>
          ) : (
            <div>
              {admins.map((admin, idx) => {
                // Permissions effectives : overrides ou permissions du rôle
                const perms = admin.permissionOverrides?.length
                  ? admin.permissionOverrides.map(p => p.name)
                  : admin.role?.permissions?.map(p => p.name) || [];

                const isSuperAdmin = perms.length === ADMIN_PERMISSIONS.length
                  || perms.includes("ADMIN_USER_MANAGE") && perms.includes("ADMIN_COMPANY_MANAGE")
                     && perms.includes("ADMIN_CREDENTIAL_MANAGE") && perms.includes("ADMIN_ROLE_MANAGE");

                return (
                  <div
                    key={admin.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr auto auto",
                      gap: 14,
                      padding: "12px 0",
                      alignItems: "center",
                      borderTop: idx > 0 ? "1px solid var(--border)" : "none",
                    }}
                  >
                    {/* Avatar */}
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,var(--accent3),var(--accent))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>
                      {admin.firstName?.[0]}{admin.lastName?.[0]}
                    </div>

                    {/* Infos */}
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-1)" }}>
                          {admin.firstName} {admin.lastName}
                        </span>
                        {isSuperAdmin && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "rgba(0,194,203,.15)", color: "var(--accent)", border: "1px solid rgba(0,194,203,.3)" }}>
                            SUPER ADMIN
                          </span>
                        )}
                        <span className={`status-badge ${admin.active ? "status-active" : "status-inactive"}`}>
                          {admin.active ? "Actif" : "Inactif"}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 6 }}>{admin.email}</div>
                      {/* Badges des permissions */}
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {ADMIN_PERMISSIONS.filter(p => perms.includes(p.name)).map(p => (
                          <span key={p.name} title={p.description}
                            style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 6, background: "rgba(255,255,255,.08)", color: "var(--text-2)" }}>
                            <i className={`bi ${p.icon} me-1`}/>
                            {p.name.replace("ADMIN_", "").replace("_", " ")}
                          </span>
                        ))}
                        {perms.length === 0 && (
                          <span style={{ fontSize: 11, color: "var(--text-3)" }}>Aucune permission</span>
                        )}
                      </div>
                    </div>

                    {/* Bouton activer/désactiver */}
                    <button
                      className="btn-icon-sm"
                      onClick={() => handleToggle(admin)}
                      title={admin.active ? "Désactiver" : "Activer"}
                      style={{ color: admin.active ? "var(--danger)" : "#69db7c" }}
                    >
                      <i className={`bi ${admin.active ? "bi-slash-circle" : "bi-check-circle"}`}/>
                    </button>

                    {/* Actions */}
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn-icon-sm"
                        onClick={() => { setEditAdmin(admin); setShowForm(true); }}
                        title="Modifier">
                        <i className="bi bi-pencil"/>
                      </button>
                      <button className="btn-icon-sm"
                        onClick={() => handleDelete(admin.id)}
                        title="Supprimer"
                        style={{ color: "var(--danger)" }}>
                        <i className="bi bi-trash"/>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════
            SECTION — Informations consultant
        ════════════════════════════════════════ 
        <div className="itm-card anim-fade-up" style={{ marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 16 }}>Informations consultant</div>
          <div className="form-row">
            <div className="form-group"><label className="itm-label">Nom / Raison sociale</label><input className="itm-input" defaultValue="Jean Dupont IT Services"/></div>
            <div className="form-group"><label className="itm-label">Email professionnel</label><input className="itm-input" type="email" defaultValue="jean@itservices.fr"/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="itm-label">Téléphone</label><input className="itm-input" defaultValue="01 23 45 67 89"/></div>
            <div className="form-group"><label className="itm-label">SIRET</label><input className="itm-input" defaultValue="123 456 789 00012"/></div>
          </div>
          <div className="form-group"><label className="itm-label">Adresse de facturation</label><input className="itm-input" defaultValue="15 rue de la Technologie, 75012 Paris"/></div>
        </div>*/}

        {/* ═══════════════════════════════════════
            SECTION — Email SMTP
        ════════════════════════════════════════ */}
        <div className="itm-card anim-fade-up delay-4" style={{ marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 16 }}>Configuration email (SMTP)</div>
          <div className="form-row">
            <div className="form-group"><label className="itm-label">Serveur SMTP</label><input className="itm-input" defaultValue="smtp.gmail.com"/></div>
            <div className="form-group"><label className="itm-label">Port</label><input className="itm-input" type="number" defaultValue={587}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="itm-label">Expéditeur</label><input className="itm-input" defaultValue="noreply@itmanagerpro.com"/></div>
            <div className="form-group"><label className="itm-label">Nom affiché</label><input className="itm-input" defaultValue="IT Manager Pro"/></div>
          </div>
          <button className="btn-itm btn-itm-outline" style={{ marginTop: 4 }}>
            <i className="bi bi-send"/> Tester la connexion
          </button>
        </div>

        {/* Enregistrer */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn-itm btn-itm-primary" onClick={save} style={{ padding: "10px 24px" }}>
            {saved
              ? <><i className="bi bi-check2"/> Paramètres enregistrés</>
              : <><i className="bi bi-floppy"/> Enregistrer tous les paramètres</>
            }
          </button>
        </div>
      </div>

      {/* Modale création / édition sous-admin */}
      {showForm && (
        <AdminFormModal
          editData={editAdmin}
          onSave={() => { setShowForm(false); setEditAdmin(null); loadAdmins(); }}
          onClose={() => { setShowForm(false); setEditAdmin(null); }}
        />
      )}

      <style>{CSS}</style>
    </>
  );
}

// ─────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────

const CSS = `
/* Badges statut */
.status-badge{font-size:11px;font-weight:600;padding:2px 8px;border-radius:20px;white-space:nowrap}
.status-active{background:rgba(47,158,68,.15);color:#69db7c;border:1px solid rgba(47,158,68,.3)}
.status-inactive{background:rgba(239,68,68,.12);color:#f87171;border:1px solid rgba(239,68,68,.25)}

/* Bouton icône */
.btn-icon-sm{width:28px;height:28px;border-radius:7px;background:none;border:1px solid transparent;color:var(--text-3);font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s}
.btn-icon-sm:hover{background:var(--border);color:var(--text-1)}

/* Ligne permission admin (formulaire) */
.admin-perm-row{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:8px;border:1px solid rgba(255,255,255,.07);cursor:pointer;transition:background .15s;margin-bottom:6px}
.admin-perm-row:hover{background:rgba(255,255,255,.05)}
.admin-perm-icon{width:28px;height:28px;border-radius:7px;background:rgba(255,255,255,.07);display:flex;align-items:center;justify-content:center;font-size:13px;color:var(--text-2);flex-shrink:0}

/* Modales */
.modal-overlay{position:fixed;inset:0;z-index:1055;background:rgba(10,12,22,.75);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;padding:16px;animation:fadeIn .15s ease}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
.modal-box{background:#1a2035;border:1px solid rgba(255,255,255,.09);border-radius:18px;box-shadow:0 40px 120px rgba(0,0,0,.6);width:100%;max-width:560px;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;animation:slideIn .2s cubic-bezier(.34,1.56,.64,1)}
@keyframes slideIn{from{transform:translateY(24px);opacity:0}to{transform:none;opacity:1}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 20px 14px;border-bottom:1px solid rgba(255,255,255,.08)}
.modal-header h3{font-size:15px;font-weight:700;color:var(--text-1);margin:0}
.modal-close{background:rgba(255,255,255,.07);border:none;cursor:pointer;color:var(--text-2);width:28px;height:28px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:12px}
.modal-close:hover{background:rgba(255,255,255,.12);color:var(--text-1)}
.modal-body{padding:18px 20px;overflow-y:auto;flex:1}
.modal-footer{padding:14px 20px;border-top:1px solid rgba(255,255,255,.08);display:flex;justify-content:flex-end;gap:10px}
.form-group{margin-bottom:14px}
.form-error{background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.25);border-radius:8px;color:var(--danger);font-size:12px;padding:8px 12px;margin-bottom:14px}
.btn-primary{background:var(--accent);border:none;border-radius:10px;color:#fff;font-size:13px;font-weight:600;padding:8px 18px;cursor:pointer;display:flex;align-items:center;gap:6px;transition:filter .15s}
.btn-primary:hover{filter:brightness(1.1)}
.btn-primary:disabled{opacity:.5;cursor:not-allowed}
.btn-secondary{background:rgba(255,255,255,.07);border:1px solid var(--border);border-radius:10px;color:var(--text-2);font-size:13px;padding:8px 18px;cursor:pointer;transition:all .15s}
.btn-secondary:hover{background:rgba(255,255,255,.12);color:var(--text-1)}
@keyframes spin{to{transform:rotate(360deg)}}
.spin{animation:spin .7s linear infinite;display:inline-block}
`;